import type {
	AccountProvider,
	RetentionSetRequest,
	TimeRange,
} from "@ccflare/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { REFRESH_INTERVALS } from "../constants";
import { queryKeys } from "../lib/query-keys";

export const useApiKeys = () => {
	return useQuery({
		queryKey: queryKeys.apiKeys(),
		queryFn: () => api.listApiKeys(),
		refetchInterval: REFRESH_INTERVALS.fast,
	});
};

// USD->display-currency rate. Changes at most daily, so refresh hourly.
export const useFxRate = () => {
	return useQuery({
		queryKey: ["fx"],
		queryFn: () => api.getFxRate(),
		staleTime: 60 * 60 * 1000,
		refetchInterval: 60 * 60 * 1000,
	});
};

export const useCreateApiKey = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ name, groupId }: { name: string; groupId?: string }) =>
			api.createApiKey(name, groupId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
		},
	});
};

export const useSetApiKeyActive = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, active }: { id: string; active: boolean }) =>
			api.setApiKeyActive(id, active),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
		},
	});
};

export const useDeleteApiKey = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id }: { id: string }) => api.deleteApiKey(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
		},
	});
};

export const useAccounts = () => {
	return useQuery({
		queryKey: queryKeys.accounts(),
		queryFn: () => api.getAccounts(),
		refetchInterval: REFRESH_INTERVALS.fast, // Refresh every 10 seconds for rate limit updates
	});
};

export const useStats = (refetchInterval?: number) => {
	return useQuery({
		queryKey: queryKeys.stats(),
		queryFn: () => api.getStats(),
		refetchInterval: refetchInterval ?? REFRESH_INTERVALS.fast,
	});
};

export const useAnalytics = (
	timeRange: TimeRange,
	filters: {
		providers?: AccountProvider[];
		accounts?: string[];
		models?: string[];
		status?: "all" | "success" | "error";
		apiKeys?: string[];
	},
	viewMode: "normal" | "cumulative",
	modelBreakdown?: boolean,
) => {
	return useQuery({
		queryKey: queryKeys.analytics(timeRange, filters, viewMode, modelBreakdown),
		queryFn: () =>
			api.getAnalytics(timeRange, filters, viewMode, modelBreakdown),
	});
};

export const useLogHistory = () => {
	return useQuery({
		queryKey: queryKeys.logHistory(),
		queryFn: () => api.getLogHistory(),
	});
};

// Mutations
export const useRemoveAccount = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ accountId }: { accountId: string }) =>
			api.removeAccount(accountId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });
		},
	});
};

export const useRenameAccount = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			accountId,
			newName,
		}: {
			accountId: string;
			newName: string;
		}) => api.renameAccount(accountId, newName),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });
		},
	});
};

export const useResetStats = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => api.resetStats(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.stats() });
		},
	});
};

// Retention settings
export const useRetention = () => {
	return useQuery({
		queryKey: ["retention"],
		queryFn: () => api.getRetention(),
	});
};

export const useSetRetention = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (partial: RetentionSetRequest) => api.setRetention(partial),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["retention"] });
		},
	});
};

export const useCleanupNow = () => {
	return useMutation({
		mutationFn: () => api.cleanupNow(),
	});
};

export const useCompactDb = () => {
	return useMutation({
		mutationFn: () => api.compactDb(),
	});
};

// Groups
export const useGroups = () => {
	return useQuery({
		queryKey: queryKeys.groups(),
		queryFn: () => api.listGroups(),
	});
};

export const useCreateGroup = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: { name: string; description?: string }) =>
			api.createGroup(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.groups() });
		},
	});
};

export const useUpdateGroup = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string;
			data: { name?: string; description?: string };
		}) => api.updateGroup(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.groups() });
		},
	});
};

export const useDeleteGroup = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id }: { id: string }) => api.deleteGroup(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.groups() });
		},
	});
};

export const useSetApiKeyGroup = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, groupId }: { id: string; groupId: string }) =>
			api.setApiKeyGroup(id, groupId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys() });
		},
	});
};

export const useSetAccountGroup = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			accountId,
			groupId,
		}: {
			accountId: string;
			groupId: string;
		}) => api.setAccountGroup(accountId, groupId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });
		},
	});
};
